import React, { FormEvent, useContext, useState } from "react";
import { Modal, Form, Row, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import useAlert from "../Alerts/useAlert";
import { LotDetailContext } from "../Contexts/LotDetailContext";
import { DialogInput } from "../DialogInputs";
import { handleInputChange } from "../Utilities/DialogUtils";
import { useHttpUtils } from "../Utilities/useHttpUtils";
import CommentDialog from "./CommentDialog";
import { DraggableModalDialog } from "./DraggableModalDialog";

interface EditRFTCodeDialogProps {
    showDialog: boolean,
    closeDialog: Function
}

export default function EditRFTCodeDialog({showDialog, closeDialog}: EditRFTCodeDialogProps) {
    const { t } = useTranslation();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { setAlert } = useAlert();
    const { lotDetail: { summary: { lotNumber, rftCode: lotRFTCode } }, getLotDetail } = useContext(LotDetailContext);

    const [loading, setLoading] = useState(false);
    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [formValidated, setFormValidated] = useState(false);
    const [dialogData, setDialogData] = useState({rftCode: lotRFTCode})
    const [errors, setErrors] = useState<any>({});

    const handleSave = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (form.checkValidity() === true) {
            setShowCommentDialog(true);
        } else {
            console.warn("Lot RFT code was invalid");
        }

        setFormValidated(true);
    };

    const closeDialogAndResetForm = () => {
        setFormValidated(false);
        closeDialog();
    };

    const updateLotRFTCode = (comment: string) => {
        const url = `/api/v1/lot/${lotNumber}/rftCode`;
        const body = {
            comment: comment,
            data: dialogData.rftCode
        };

        setLoading(true);
        fetchDataAuthenticated(url, 'PUT', JSON.stringify(body))
            .then(async response => {
                if (response.ok) {
                    setAlert("success", "Success", `Updated the lot RFT Code`);
                    getLotDetail();
                } else {
                    let errorMessage = await response.text();
                    console.log("Failed to update the lot RFT Code: " + errorMessage);
                    throw new Error("Failed to update the lot RFT Code");
                }
                closeDialog();
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const onChange = (e: any) => {
        handleInputChange(e, dialogData, setDialogData, errors, setErrors);
    };

    return (
        <>
            <Modal dialogAs={DraggableModalDialog} show={showDialog} onHide={closeDialogAndResetForm} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{t<string>("Dialogs.RFTCode")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loading
                        ?
                        <div className="d-flex justify-content-center mb-2">
                            <Spinner animation="border" />
                        </div>
                        :
                        <Form id="setLotRFTCode" noValidate validated={formValidated} onSubmit={handleSave}>
                            <Form.Group as={Row} className="mb-2">
                                <DialogInput controlId="formRFTCode" title="RFT Code" name="rftCode" value={dialogData.rftCode} onChange={onChange} placeholder={undefined} error={errors.rftCode} />
                            </Form.Group>
                        </Form>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDialogAndResetForm} disabled={loading}>
                        {t<string>("Application.Controls.Cancel")}
                    </Button>
                    <Button variant="primary" type="submit" form="setLotRFTCode" disabled={loading}>
                        {t<string>("Application.Controls.Save")}
                    </Button>
                </Modal.Footer>
            </Modal>
            <CommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} onSave={updateLotRFTCode} />
        </>
    );
}